import { Box } from '@lego/klik-ui';
import { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const markdownStyles = {
  '& > *:first-of-type': { marginTop: 0 },
  '& > *:last-child': { marginBottom: 0 },
  '& a': { color: 'light-blue.600', textDecoration: 'underline' },
  '& strong': { fontWeight: 'bold' },
  '& em': { fontStyle: 'italic' },
  '& del': { textDecoration: 'line-through' },
  '& code': {
    background: 'slate.200',
    borderRadius: 'sm',
    fontFamily: 'mono',
    fontSize: 'xs',
    padding: '1px 4px',
  },
  '& pre': {
    background: 'slate.200',
    borderRadius: 'md',
    marginBottom: 2,
    overflowX: 'auto',
    padding: 2,
  },
  '& pre code': {
    background: 'transparent',
    display: 'block',
    fontSize: 'xs',
    padding: 0,
  },
  '& h1': { fontSize: 'lg', fontWeight: 'bold', marginBottom: 2, marginTop: 3 },
  '& h2': { fontSize: 'md', fontWeight: 'bold', marginBottom: 2, marginTop: 3 },
  '& h3': { fontSize: 'sm', fontWeight: 'bold', marginBottom: 1, marginTop: 2 },
  '& h4, & h5, & h6': {
    fontSize: 'sm',
    fontWeight: 'semibold',
    marginBottom: 1,
    marginTop: 2,
  },
  '& p': { marginBottom: 2 },
  '& ul, & ol': { marginBottom: 2, paddingLeft: 5 },
  '& ul': { listStyleType: 'disc' },
  '& ol': { listStyleType: 'decimal' },
  '& li': { marginBottom: 1 },
  '& li > ul, & li > ol': { marginBottom: 0, marginTop: 1 },
  '& blockquote': {
    borderLeft: '3px solid',
    borderLeftColor: 'slate.300',
    color: 'slate.600',
    marginBottom: 2,
    paddingLeft: 3,
  },
  '& hr': {
    borderColor: 'slate.300',
    marginY: 3,
  },
  '& table': {
    borderCollapse: 'collapse',
    display: 'block',
    marginBottom: 2,
    overflowX: 'auto',
    width: '100%',
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'slate.300',
    padding: '4px 8px',
    textAlign: 'left',
  },
  '& th': { background: 'slate.200', fontWeight: 'semibold' },
  '& img': { borderRadius: 'md', maxWidth: '100%' },
};

// Memoized so re-renders of the streaming message (once per reveal frame) don't re-parse markdown
// when the revealed text hasn't changed.
export const Markdown = memo(({ children }: { children: string }) => {
  return (
    <Box sx={markdownStyles}>
      <ReactMarkdown
        components={{
          a: ({ children, href }) =>
            href ? (
              <a href={href} rel="noopener noreferrer" target="_blank">
                {children}
              </a>
            ) : (
              children
            ),
        }}
        remarkPlugins={[remarkGfm]}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
});

Markdown.displayName = 'Markdown';
